/**
 * Future Home Plan Foundation (FHPF) - Core Financial Calculations
 * Timezone: Asia/Dhaka (UTC+6, fixed offset with zero DST shifts)
 */

import type { Member, Contribution, MemberFinancialSummary } from '../types/index.ts';

export const GROUP_DEFAULTS = {
  MONTHLY_AMOUNT: 5000,
  LATE_FEE: 200,
  DUE_DAY: 10, // 10th day of the month
  CUTOFF_HOUR: 23,
  CUTOFF_MINUTE: 59,
  CUTOFF_SECOND: 59,
  TIMEZONE: 'Asia/Dhaka',
  DHAKA_OFFSET_HOURS: 6,
  DHAKA_OFFSET_MS: 6 * 60 * 60 * 1000,
} as const;

export interface DhakaDateTime {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
  isoMonth: string; // 'YYYY-MM'
  formattedString: string; // 'YYYY-MM-DD HH:mm:ss'
}

/**
 * Converts any UTC or local Date into an exact Asia/Dhaka calendar object.
 * Bangladesh operates on UTC+6 year-round without daylight saving time.
 */
export function toDhakaDateTime(date: Date = new Date()): DhakaDateTime {
  const utcMs = date.getTime();
  const dhakaMs = utcMs + GROUP_DEFAULTS.DHAKA_OFFSET_MS;
  const dhakaDate = new Date(dhakaMs);

  const year = dhakaDate.getUTCFullYear();
  const month = dhakaDate.getUTCMonth() + 1;
  const day = dhakaDate.getUTCDate();
  const hour = dhakaDate.getUTCHours();
  const minute = dhakaDate.getUTCMinutes();
  const second = dhakaDate.getUTCSeconds();

  const pad = (n: number) => n.toString().padStart(2, '0');
  const isoMonth = `${year}-${pad(month)}`;
  const formattedString = `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}:${pad(second)}`;

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    isoMonth,
    formattedString,
  };
}

/**
 * Returns the exact timestamp (in UTC milliseconds) for the payment deadline
 * of a given contribution month (YYYY-MM).
 * Deadline is strictly the 10th day at 23:59:59.999 Asia/Dhaka time.
 */
export function getDhakaMonthDeadlineUtcMs(monthStr: string): number {
  const [yearStr, monthNumStr] = monthStr.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthNumStr, 10); // 1-12

  // Construct UTC timestamp representing 10th day 23:59:59.999 in Dhaka (UTC+6)
  // In UTC, this is 10th day 17:59:59.999 UTC
  const deadlineUtc = Date.UTC(year, month - 1, GROUP_DEFAULTS.DUE_DAY, 23 - GROUP_DEFAULTS.DHAKA_OFFSET_HOURS, 59, 59, 999);
  return deadlineUtc;
}

/**
 * Determines whether a submission timestamp is past the deadline for the specified month.
 * Returns true if late, false if on time.
 */
export function isLateSubmission(monthStr: string, submissionDate: Date): boolean {
  const deadlineMs = getDhakaMonthDeadlineUtcMs(monthStr);
  return submissionDate.getTime() > deadlineMs;
}

/**
 * Calculates applicable late fee.
 * If submission is on or before the 10th 23:59:59 Dhaka, lateFee is 0.
 * If submission is after deadline, lateFee is 200 BDT.
 */
export function calculateLateFee(monthStr: string, submissionDate: Date): number {
  return isLateSubmission(monthStr, submissionDate) ? GROUP_DEFAULTS.LATE_FEE : 0;
}

/**
 * Generates an array of YYYY-MM strings between startMonth and endMonth inclusive.
 */
export function getMonthsBetween(startMonthStr: string, endMonthStr: string): string[] {
  const [startYear, startM] = startMonthStr.split('-').map(Number);
  const [endYear, endM] = endMonthStr.split('-').map(Number);

  const months: string[] = [];
  let curYear = startYear;
  let curMonth = startM;

  while (curYear < endYear || (curYear === endYear && curMonth <= endM)) {
    const pad = (n: number) => n.toString().padStart(2, '0');
    months.push(`${curYear}-${pad(curMonth)}`);

    curMonth++;
    if (curMonth > 12) {
      curMonth = 1;
      curYear++;
    }
  }

  return months;
}

/**
 * Calculates complete dues, advance payments, and member financial summary.
 */
export function calculateMemberFinancials(params: {
  member: Member;
  groupStartDate: string; // 'YYYY-MM-DD' or 'YYYY-MM'
  currentDate?: Date;
  approvedContributions: Contribution[];
}): MemberFinancialSummary {
  const { member, groupStartDate, currentDate = new Date(), approvedContributions } = params;

  const currentDhaka = toDhakaDateTime(currentDate);

  // Group start month
  const groupStartMonth = groupStartDate.substring(0, 7);
  // Member join month
  const memberJoinMonth = member.joinDate.substring(0, 7);

  // Effective obligation start month is the later of groupStartMonth or memberJoinMonth
  const effectiveStartMonth = memberJoinMonth > groupStartMonth ? memberJoinMonth : groupStartMonth;

  // If member exited, effective end month is exit month; otherwise current calendar month in Dhaka
  let effectiveEndMonth = currentDhaka.isoMonth;
  if (member.status === 'exited' && member.exitDate) {
    const memberExitMonth = member.exitDate.substring(0, 7);
    if (memberExitMonth < effectiveEndMonth) {
      effectiveEndMonth = memberExitMonth;
    }
  }

  // Generate list of all months member is expected to contribute
  const expectedMonths = effectiveStartMonth <= effectiveEndMonth
    ? getMonthsBetween(effectiveStartMonth, effectiveEndMonth)
    : [];

  const totalMonthsExpected = expectedMonths.length;
  const expectedContributions = totalMonthsExpected * GROUP_DEFAULTS.MONTHLY_AMOUNT;

  // Filter approved contributions belonging to this member
  const memberApproved = approvedContributions.filter(
    (c) => c.memberId === member.uid && c.status === 'approved'
  );

  // Calculate approved totals
  let approvedBaseTotal = 0;
  let approvedLateFeesTotal = 0;
  const approvedMonthsSet = new Set<string>();

  for (const contrib of memberApproved) {
    approvedBaseTotal += contrib.amount;
    approvedLateFeesTotal += contrib.lateFee || 0;
    approvedMonthsSet.add(contrib.month);
  }

  const approvedTotal = approvedBaseTotal + approvedLateFeesTotal;

  // Calculate unpaid expected months
  const unpaidMonths = expectedMonths.filter((m) => !approvedMonthsSet.has(m));

  // Calculate expected late fees for unpaid past months whose deadline has passed
  let pendingLateFees = 0;
  for (const unpaidMonth of unpaidMonths) {
    // If the deadline for this unpaid month has passed relative to current date, late fee applies
    if (isLateSubmission(unpaidMonth, currentDate)) {
      pendingLateFees += GROUP_DEFAULTS.LATE_FEE;
    }
  }

  const expectedLateFees = approvedLateFeesTotal + pendingLateFees;
  const expectedTotal = expectedContributions + expectedLateFees;

  const dueAmount = Math.max(0, expectedTotal - approvedTotal);
  const advanceAmount = Math.max(0, approvedTotal - expectedTotal);

  return {
    memberId: member.uid,
    name: member.name,
    status: member.status,
    joinDate: member.joinDate,
    totalMonthsExpected,
    expectedContributions,
    expectedLateFees,
    expectedTotal,
    approvedTotal,
    dueAmount,
    advanceAmount,
    approvedMonthsCount: approvedMonthsSet.size,
    unpaidMonths,
  };
}
